import React, { useEffect, useState, useCallback } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { LoadingSpinner } from "../LoadingSpinner";
import { useApiService } from "../../services/apiService";
import { Alert, AlertDescription } from "../ui/alert";
import { Separator } from "../ui/separator";
import { FileText, Calendar, CheckCircle2, AlertCircle, Users, Database, RefreshCw } from "lucide-react";
import { FormSelection, type HubSpotForm } from "./FormSelection";

export const BackfillForms: React.FC = () => {
  const apiService = useApiService();
  const [forms, setForms] = useState<HubSpotForm[]>([]);
  const [selectedForms, setSelectedForms] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load forms function with proper cleanup
  const loadForms = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiService.get<HubSpotForm[]>("/hubspot/forms");
      setForms(data);
    } catch (err) {
      setError("Failed to load forms. Please check your connection and try again.");
      console.error("Error loading forms:", err);
    } finally {
      setLoading(false);
    }
  }, [apiService]);

  // Load forms only once when component mounts - FIXED: Prevent infinite calls
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      if (!mounted) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await apiService.get("/hubspot/forms");
        if (mounted) {
          setForms(data as HubSpotForm[]);
        }
      } catch (err) {
        if (mounted) {
          setError("Failed to load forms. Please check your connection and try again.");
          console.error("Error loading forms:", err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - load only once on mount

  const handleFormToggle = (formId: string, checked: boolean) => {
    const newSelection = new Set(selectedForms);
    if (checked) {
      newSelection.add(formId);
    } else {
      newSelection.delete(formId);
    }
    setSelectedForms(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedForms(new Set(forms.map(form => form.id)));
    } else {
      setSelectedForms(new Set());
    }
  };

  const handleBackfill = async () => {
    if (selectedForms.size === 0) {
      setError("Please select at least one form to backfill.");
      return;
    }

    setBackfillLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const formIds = Array.from(selectedForms);
      await apiService.post("/hubspot/forms/backfill", { 
        formIds,
        selectedFormsCount: formIds.length 
      });
      setSuccess(`Forms backfill started successfully for ${formIds.length} selected form${formIds.length > 1 ? 's' : ''}.`);
      // Clear selection after successful backfill
      setSelectedForms(new Set());
    } catch (err) {
      setError("Failed to start forms backfill. Please try again.");
      console.error("Error starting backfill:", err);
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleRefresh = () => {
    loadForms();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <FileText className="h-8 w-8" />
          HubSpot Forms Backfill
        </h1>
        <p className="text-muted-foreground">
          Select and backfill HubSpot forms to synchronize them with your local database.
        </p>
      </div>

      <Separator />

      {/* Action Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" />
            Backfill Actions
          </CardTitle>
          <CardDescription>
            Select the forms you want to backfill and start the synchronization process.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary and Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="text-sm">
                {selectedForms.size} of {forms.length} selected
              </Badge>
              <span className="text-sm text-muted-foreground">
                {forms.length} form{forms.length !== 1 ? 's' : ''} available
              </span>
            </div>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="mr-2 h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </>
              )}
            </Button>
          </div>

          {/* Action Button */}
          <div className="flex items-center gap-4">
            <Button 
              onClick={handleBackfill} 
              disabled={backfillLoading || selectedForms.size === 0 || loading}
              className="min-w-[200px]"
            >
              {backfillLoading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  Starting Backfill...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Backfill {selectedForms.size > 0 ? selectedForms.size : ''} Form{selectedForms.size !== 1 ? 's' : ''}
                </>
              )}
            </Button>
            {selectedForms.size > 0 && (
              <Button 
                variant="outline" 
                onClick={() => setSelectedForms(new Set())}
                disabled={backfillLoading}
              >
                Clear Selection
              </Button>
            )}
          </div>

          {/* Status Messages */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Forms Selection - Using Enhanced FormSelection Component */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Available Forms
          </CardTitle>
          <CardDescription>
            Search and filter forms, then select the ones you want to backfill.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
              <span className="ml-2 text-muted-foreground">Loading forms...</span>
            </div>
          ) : error && forms.length === 0 ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : forms.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No forms found. Try refreshing the page or check your HubSpot connection.</p>
            </div>
          ) : (
            <FormSelection
              forms={forms}
              selectedForms={selectedForms}
              onFormToggle={handleFormToggle}
              onSelectAll={handleSelectAll}
              loading={loading}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
